import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpRight, Loader2, Mail, MessageCircle, Phone } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { SidePanel } from '../../../components/design-system/common/SidePanel';
import { MediaLightbox, LightboxMedia } from '../../../components/design-system/common/MediaLightbox';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { DESTRUCTIVE_OUTLINE } from '../../../components/design-system/admin/styles';
import { formatDateTime } from '../../../components/design-system/admin/format';
import { Thumb } from '../../../components/design-system/analytics/Thumb';
import { useShopperRequestsApi } from '../../../common/hooks/use-shopper-requests-api.hook';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { config } from '../../../common/hooks/use-settings-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import {
    areaFigure,
    formatDay,
    formatMoney,
    itemsArea,
    sourceKey,
    statusActions,
    unitLabel,
    upperKey,
    whoOf,
} from './request-format';
import type { Translate } from './request-format';
import { buildReplyMessage, mailtoLink, replyLanguage, telLink, whatsappLink } from './reply-message';
import { KindTile, RequestStatusBadge, TestBadge } from './request-presentation';
import type { ContactChannel, RequestDetail, RequestItem, RequestPatch, RequestStatus, RequestView } from './types';

const KNOWN_SURFACES = ['floor', 'wall', 'walls', 'ceiling', 'backsplash'];

interface ReplyChannel {
    id: ContactChannel;
    href: string;
    label: string;
    icon: React.ReactNode;
    external: boolean;
}

/**
 * One request, everything needed to answer it: what they saw (the render,
 * their room), who they are and how they want to be reached, what they chose
 * and the estimate they were shown, their other requests — and the reply,
 * prefilled in their language, one tap from WhatsApp, email or the phone.
 */
export function RequestDrawer({
    uuid,
    initial,
    onClose,
    onChanged,
    onOpenOther,
}: {
    uuid: string | null;
    /** The list row, shown while the full request loads. */
    initial: RequestView | null;
    onClose: () => void;
    onChanged: (request: RequestDetail) => void;
    onOpenOther: (uuid: string) => void;
}) {
    const { t, i18n } = useTranslation();
    const api = useShopperRequestsApi();
    const { user } = useAuthContext();
    const [detail, setDetail] = useState<RequestDetail | null>(null);
    const [missing, setMissing] = useState(false);
    const [note, setNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [busy, setBusy] = useState<RequestStatus | null>(null);
    const [media, setMedia] = useState<LightboxMedia | null>(null);

    useEffect(() => {
        if (!uuid) return undefined;
        let alive = true;
        setDetail(null);
        setMissing(false);
        api.get(uuid)
            .then((d) => {
                if (!alive) return;
                setDetail(d);
                setNote(d?.vendorNote ?? '');
            })
            .catch(() => alive && setMissing(true));
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    const shown: RequestView | null = detail ?? (initial && initial.uuid === uuid ? initial : null);
    const vendorName = user?.vendor?.name || config?.company || '';
    const lang = replyLanguage(shown?.lang, i18n.language);

    const reply = useMemo(() => {
        if (!shown) return null;
        const fixed = i18n.getFixedT(lang);
        const inTheirLanguage: Translate = (key, options) => String(fixed(key, options as any));
        const message = buildReplyMessage(shown, inTheirLanguage, { lang, vendorName });
        return {
            whatsapp: whatsappLink(shown.contact?.whatsapp, message.body),
            email: mailtoLink(shown.contact?.email, message.subject, message.body),
            phone: telLink(shown.contact?.phone),
        };
    }, [shown, lang, vendorName, i18n]);

    const update = async (patch: RequestPatch, success?: string) => {
        if (!shown) return null;
        try {
            const next = await api.patch(shown.uuid, patch);
            setDetail(next);
            onChanged(next);
            if (success) toast.success(success);
            return next;
        } catch (err) {
            const body = await readApiError(err);
            toast.error(body.message ? String(body.message) : t('SHOPPER_REQUESTS.DRAWER.UPDATE_FAILED'));
            return null;
        }
    };

    const setStatus = async (to: RequestStatus) => {
        setBusy(to);
        await update({ status: to }, t('SHOPPER_REQUESTS.DRAWER.MARKED', { status: t(`SHOPPER_REQUESTS.STATUS.${upperKey(to)}`) }));
        setBusy(null);
    };

    const saveNote = async () => {
        if (!shown || (note.trim() === (detail?.vendorNote ?? '').trim())) return;
        setSavingNote(true);
        await update({ vendorNote: note.trim() }, t('SHOPPER_REQUESTS.DRAWER.NOTE_SAVED'));
        setSavingNote(false);
    };

    const who = shown ? whoOf(shown) || t('SHOPPER_REQUESTS.ANONYMOUS') : '';
    const title = shown ? who : t('SHOPPER_REQUESTS.DRAWER.LOADING');
    const description = shown ? t(`SHOPPER_REQUESTS.KIND_TITLE.${upperKey(shown.kind)}`) : undefined;

    // The channel the shopper asked to be answered on goes first, solid; the others outlined.
    const preferred = shown?.contact?.preferred;
    const channels: ReplyChannel[] = reply
        ? [
              { id: 'whatsapp' as const, href: reply.whatsapp, label: t('SHOPPER_REQUESTS.DRAWER.WHATSAPP'), icon: <MessageCircle />, external: true },
              { id: 'email' as const, href: reply.email, label: t('SHOPPER_REQUESTS.DRAWER.EMAIL_ACTION'), icon: <Mail />, external: false },
              { id: 'phone' as const, href: reply.phone, label: t('SHOPPER_REQUESTS.DRAWER.CALL'), icon: <Phone />, external: false },
          ]
              .filter((c) => !!c.href)
              .sort((a, b) => Number(b.id === preferred) - Number(a.id === preferred))
        : [];

    return (
        <>
            <SidePanel
                open={!!uuid}
                onOpenChange={(open) => !open && onClose()}
                title={title}
                description={description}
                width="data-[side=right]:sm:max-w-[640px]"
            >
                <div className="flex flex-col gap-6" data-testid="sr-drawer" data-uuid={uuid ?? undefined}>
                    {missing && !shown ? (
                        <p className="text-sm text-muted-foreground">{t('SHOPPER_REQUESTS.DRAWER.NOT_FOUND')}</p>
                    ) : !shown ? (
                        <div className="flex flex-col gap-3">
                            <Skeleton className="aspect-video w-full rounded-xl" />
                            <Skeleton className="h-4 w-56" />
                            <Skeleton className="h-4 w-40" />
                        </div>
                    ) : (
                        <>
                            {/* What it is, where it stands, since when. */}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                <KindTile kind={shown.kind} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium">{t(`SHOPPER_REQUESTS.KIND_TITLE.${upperKey(shown.kind)}`)}</span>
                                        <RequestStatusBadge status={shown.status} />
                                        {shown.internal && <TestBadge />}
                                    </div>
                                    <p className="mt-0.5 text-xs text-muted-foreground">
                                        {t(`SHOPPER_REQUESTS.SOURCE.${sourceKey(shown.source)}`)} · <RelativeTime value={shown.createdAt} /> · {formatDateTime(shown.createdAt)}
                                    </p>
                                </div>
                            </div>

                            {/* Reply first: that is why the drawer was opened. */}
                            <section className="flex flex-col gap-2" aria-label={t('SHOPPER_REQUESTS.DRAWER.REPLY')}>
                                {channels.length ? (
                                    <div className="flex flex-wrap gap-2">
                                        {channels.map((c, i) => (
                                            <Button key={c.id} asChild variant={i === 0 ? 'default' : 'outline'} size="sm" data-testid={`sr-reply-${c.id}`}>
                                                <a href={c.href} {...(c.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                                                    {c.icon}
                                                    {c.label}
                                                </a>
                                            </Button>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">{t('SHOPPER_REQUESTS.DRAWER.NO_CHANNEL')}</p>
                                )}
                                {channels.some((c) => c.id !== 'phone') && (
                                    <p className="text-xs text-muted-foreground">
                                        {t('SHOPPER_REQUESTS.DRAWER.REPLY_HINT', { language: t(`SHOPPER_REQUESTS.LANGUAGE_NAME.${lang.toUpperCase()}`) })}
                                    </p>
                                )}
                            </section>

                            {(shown.renderImageUrl || shown.roomImageUrl) && (
                                <section className="flex items-end gap-3">
                                    {shown.renderImageUrl && (
                                        <button
                                            type="button"
                                            className="min-w-0 flex-1 overflow-hidden rounded-xl border"
                                            onClick={() => setMedia({ url: shown.renderImageUrl!, kind: 'image', title: t('SHOPPER_REQUESTS.DRAWER.RENDER') })}
                                            aria-label={t('SHOPPER_REQUESTS.DRAWER.RENDER')}
                                        >
                                            <img src={shown.renderImageUrl} alt={t('SHOPPER_REQUESTS.DRAWER.RENDER')} className="aspect-video w-full object-cover" />
                                        </button>
                                    )}
                                    {shown.roomImageUrl && (
                                        <figure className="flex w-24 shrink-0 flex-col gap-1">
                                            <button
                                                type="button"
                                                className="overflow-hidden rounded-lg border"
                                                onClick={() => setMedia({ url: shown.roomImageUrl!, kind: 'image', title: t('SHOPPER_REQUESTS.DRAWER.ROOM') })}
                                                aria-label={t('SHOPPER_REQUESTS.DRAWER.ROOM')}
                                            >
                                                <img src={shown.roomImageUrl} alt={t('SHOPPER_REQUESTS.DRAWER.ROOM')} className="aspect-square w-full object-cover" />
                                            </button>
                                            <figcaption className="text-center text-[11px] text-muted-foreground">{t('SHOPPER_REQUESTS.DRAWER.ROOM')}</figcaption>
                                        </figure>
                                    )}
                                </section>
                            )}

                            {shown.includeRoomImage === false && (
                                <p className="text-xs text-muted-foreground" data-testid="request-no-room-picture">{t('SHOPPER_REQUESTS.DRAWER.NO_ROOM_PICTURE')}</p>
                            )}

                            <ContactBlock request={shown} lang={lang} />

                            {shown.message && (
                                <section>
                                    <h3 className="mb-2 text-sm font-medium">{t('SHOPPER_REQUESTS.DRAWER.MESSAGE')}</h3>
                                    <p className="rounded-lg border p-3 text-sm whitespace-pre-wrap" dir="auto">
                                        {shown.message}
                                    </p>
                                </section>
                            )}

                            <ItemsBlock request={shown} />

                            {(shown.leadId || (detail?.others?.length ?? 0) > 0) && (
                                <section className="flex flex-col gap-2">
                                    <div className="flex items-center justify-between gap-3">
                                        <h3 className="text-sm font-medium">{t('SHOPPER_REQUESTS.DRAWER.OTHERS')}</h3>
                                        {shown.leadId && (
                                            <Button asChild variant="outline" size="sm" data-testid="sr-open-lead">
                                                <Link to={`/sales/leads/${shown.leadId}`}>
                                                    {t('SHOPPER_REQUESTS.DRAWER.OPEN_LEAD')}
                                                    <ArrowUpRight />
                                                </Link>
                                            </Button>
                                        )}
                                    </div>
                                    {detail?.others?.length ? (
                                        <ul className="divide-y rounded-lg border">
                                            {detail.others.map((o) => (
                                                <li key={o.uuid}>
                                                    <button
                                                        type="button"
                                                        onClick={() => onOpenOther(o.uuid)}
                                                        className="flex w-full items-center gap-3 px-3 py-2 text-start transition-colors hover:bg-muted/40"
                                                    >
                                                        <KindTile kind={o.kind} size="sm" />
                                                        <span className="min-w-0 flex-1 truncate text-sm">{t(`SHOPPER_REQUESTS.KIND_TITLE.${upperKey(o.kind)}`)}</span>
                                                        <RequestStatusBadge status={o.status} />
                                                        <RelativeTime value={o.createdAt} className="w-24 shrink-0 text-end text-xs text-muted-foreground" />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-xs text-muted-foreground">{t('SHOPPER_REQUESTS.DRAWER.NO_OTHERS')}</p>
                                    )}
                                </section>
                            )}

                            <section className="flex flex-col gap-2">
                                <label htmlFor="sr-note" className="text-sm font-medium">
                                    {t('SHOPPER_REQUESTS.DRAWER.NOTE')}
                                </label>
                                <Textarea
                                    id="sr-note"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    onBlur={() => void saveNote()}
                                    placeholder={t('SHOPPER_REQUESTS.DRAWER.NOTE_PLACEHOLDER')}
                                    rows={3}
                                    maxLength={2000}
                                    disabled={!detail}
                                    data-testid="sr-note"
                                />
                                <div className="flex justify-end">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => void saveNote()}
                                        disabled={!detail || savingNote || note.trim() === (detail?.vendorNote ?? '').trim()}
                                    >
                                        {savingNote && <Loader2 className="animate-spin" />}
                                        {t('SHOPPER_REQUESTS.DRAWER.NOTE_SAVE')}
                                    </Button>
                                </div>
                            </section>

                            <section className="flex flex-col gap-2 border-t pt-5">
                                <h3 className="text-sm font-medium">{t('SHOPPER_REQUESTS.DRAWER.STATUS_TITLE')}</h3>
                                {shown.handledAt && (
                                    <p className="text-xs text-muted-foreground">
                                        {shown.handledBy
                                            ? t('SHOPPER_REQUESTS.DRAWER.HANDLED_BY', { by: shown.handledBy, when: formatDateTime(shown.handledAt) })
                                            : t('SHOPPER_REQUESTS.DRAWER.HANDLED', { when: formatDateTime(shown.handledAt) })}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    {statusActions(shown.kind, shown.status).map((a) => (
                                        <Button
                                            key={a.key}
                                            variant="outline"
                                            size="sm"
                                            className={cn(a.destructive && DESTRUCTIVE_OUTLINE)}
                                            disabled={!!busy || !detail}
                                            onClick={() => void setStatus(a.to)}
                                            data-testid={`sr-status-${a.to}`}
                                        >
                                            {busy === a.to && <Loader2 className="animate-spin" />}
                                            {t(`SHOPPER_REQUESTS.ACTIONS.${a.key}`)}
                                        </Button>
                                    ))}
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </SidePanel>
            <MediaLightbox media={media} onClose={() => setMedia(null)} />
        </>
    );
}

/** Who they are, how they want to be reached, and the visit they asked for. */
function ContactBlock({ request, lang }: { request: RequestView; lang: string }) {
    const { t, i18n } = useTranslation();
    const c = request.contact ?? {};
    const day = formatDay(request.preferredDate, i18n.language, 'long');
    const rows: Array<{ label: string; value: React.ReactNode; testId?: string }> = [];
    if (c.name) rows.push({ label: t('SHOPPER_REQUESTS.DRAWER.NAME'), value: c.name });
    if (c.email) rows.push({ label: t('SHOPPER_REQUESTS.DRAWER.EMAIL'), value: <a className="underline-offset-4 hover:underline" href={`mailto:${c.email}`}>{c.email}</a> });
    if (c.phone) rows.push({ label: t('SHOPPER_REQUESTS.DRAWER.PHONE'), value: <a className="tabular-nums underline-offset-4 hover:underline" href={telLink(c.phone)} dir="ltr">{c.phone}</a> });
    if (c.preferred) rows.push({ label: t('SHOPPER_REQUESTS.DRAWER.PREFERRED'), value: t(`SHOPPER_REQUESTS.CHANNEL.${upperKey(c.preferred)}`) });
    rows.push({
        label: t('SHOPPER_REQUESTS.DRAWER.MARKETING'),
        value: request.consent?.marketing ? t('SHOPPER_REQUESTS.DRAWER.MARKETING_YES') : t('SHOPPER_REQUESTS.DRAWER.MARKETING_NO'),
    });
    if (request.lang) rows.push({ label: t('SHOPPER_REQUESTS.DRAWER.LANGUAGE'), value: t(`SHOPPER_REQUESTS.LANGUAGE_NAME.${lang.toUpperCase()}`) });
    if (request.store) {
        rows.push({
            label: t('SHOPPER_REQUESTS.DRAWER.STORE'),
            value: (
                <span>
                    {request.store.name}
                    {(request.store.address || typeof request.store.distanceKm === 'number') && (
                        <span className="block text-xs text-muted-foreground">
                            {[
                                request.store.address,
                                typeof request.store.distanceKm === 'number'
                                    ? t('SHOPPER_REQUESTS.DRAWER.DISTANCE', { km: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(request.store.distanceKm) })
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </span>
                    )}
                </span>
            ),
        });
    }
    if (day) {
        rows.push({
            label: t('SHOPPER_REQUESTS.DRAWER.VISIT_DATE'),
            value: request.preferredSlot ? `${day} · ${t(`SHOPPER_REQUESTS.SLOT.${upperKey(request.preferredSlot)}`)}` : day,
        });
    }
    return (
        <section>
            <h3 className="mb-3 text-sm font-medium">{t('SHOPPER_REQUESTS.DRAWER.CONTACT')}</h3>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                {rows.map((r) => (
                    <div key={r.label} className="min-w-0">
                        <dt className="text-xs text-muted-foreground">{r.label}</dt>
                        <dd className="mt-0.5 break-words">{r.value}</dd>
                    </div>
                ))}
            </dl>
        </section>
    );
}

/** The products they chose, with the figures they were shown, and the estimate as a whole. */
function ItemsBlock({ request }: { request: RequestView }) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const items = request.items ?? [];
    const area = areaFigure(request.areaM2 ?? itemsArea(items), request.areaRange, lang);
    const total = formatMoney(request.total, request.currency, lang);
    if (!items.length && !area && !total) return null;

    return (
        <section>
            <h3 className="mb-3 text-sm font-medium">{t('SHOPPER_REQUESTS.DRAWER.ITEMS')}</h3>
            {items.length > 0 && (
                <ul className="divide-y rounded-lg border" data-testid="sr-items">
                    {items.map((item, i) => (
                        <ItemRow key={`${item.productUuid}-${i}`} item={item} currency={request.currency} />
                    ))}
                </ul>
            )}
            {(area || total) && (
                <div className="mt-3 flex flex-col gap-1">
                    <dl className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm">
                        {area && (
                            <div className="flex items-baseline gap-2">
                                <dt className="text-xs text-muted-foreground">{t('SHOPPER_REQUESTS.DRAWER.AREA_TOTAL')}</dt>
                                <dd className="tabular-nums">{t(area.kind === 'range' ? 'SHOPPER_REQUESTS.AREA_RANGE' : 'SHOPPER_REQUESTS.AREA_ABOUT', { area: area.text })}</dd>
                            </div>
                        )}
                        {total && (
                            <div className="flex items-baseline gap-2">
                                <dt className="text-xs text-muted-foreground">{t('SHOPPER_REQUESTS.DRAWER.ESTIMATE_TOTAL')}</dt>
                                <dd className="font-medium tabular-nums">{total}</dd>
                            </div>
                        )}
                    </dl>
                    <p className="text-end text-xs text-muted-foreground">{t('SHOPPER_REQUESTS.DRAWER.ESTIMATE_CAPTION')}</p>
                </div>
            )}
        </section>
    );
}

function ItemRow({ item, currency }: { item: RequestItem; currency: string | null }) {
    const { t: rawT, i18n } = useTranslation();
    const t: Translate = (key, options) => String(rawT(key, options as any));
    const lang = i18n.language;
    const area = areaFigure(item.areaM2, null, lang);
    const unitPrice = formatMoney(item.unitPrice, currency, lang);
    const lineTotal = formatMoney(item.total, currency, lang);
    const surface = item.surface ? (KNOWN_SURFACES.includes(item.surface.toLowerCase()) ? t(`SHOPPER_REQUESTS.SURFACE.${item.surface.toUpperCase()}`) : item.surface) : null;
    const quantity =
        typeof item.boxes === 'number' && item.boxes > 0
            ? t('SHOPPER_REQUESTS.DRAWER.BOXES', { count: item.boxes })
            : typeof item.quantity === 'number' && item.quantity > 0
              ? `${new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(item.quantity)}${item.unit ? ` ${unitLabel(item.unit, t)}` : ''}`
              : null;
    const unit = item.unit ? unitLabel(item.unit, t) : null;

    return (
        <li className="flex items-start gap-3 p-3">
            <Thumb src={item.thumbnailUrl} alt="" size="md" />
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{item.title}</p>
                {item.sku && <p className="truncate text-xs text-muted-foreground" dir="ltr">{item.sku}</p>}
                {(surface || area || quantity || unitPrice) && (
                    <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs tabular-nums text-muted-foreground">
                        {surface && <span>{surface}</span>}
                        {area && <span>{t('SHOPPER_REQUESTS.AREA_ABOUT', { area: area.text })}</span>}
                        {quantity && <span>{quantity}</span>}
                        {unitPrice && <span>{unit ? `${unitPrice} / ${unit}` : unitPrice}</span>}
                    </p>
                )}
            </div>
            {lineTotal && <span className="shrink-0 text-sm tabular-nums">{lineTotal}</span>}
        </li>
    );
}
